import type { JobNotificationChange } from "../../../../../utils/jobNotificationTypes";

/** Switches the admin changed in edit mode. Other jobs use their baseline value. */
export type JobSilentOverrides = Readonly<Record<string, boolean>>;

/** Mirrors the backend: a new job starts silent, unless it is the primary. Only used for jobs added in this edit. */
export const isNewJobSilentByDefault = (finalJobIds: readonly string[], jobId: string): boolean => finalJobIds[0] !== jobId;

/** Mirrors the backend: a job that becomes the primary is turned on, even a silent one. */
const becomesPrimary = (originalJobIds: readonly string[], finalJobIds: readonly string[], jobId: string): boolean =>
	finalJobIds[0] === jobId && originalJobIds[0] !== jobId;

type DraftContext = {
	/** The job list when the edit started (empty for a new profile). */
	originalJobIds: readonly string[];
	/** The job list being saved. The first entry is the primary job. */
	finalJobIds: readonly string[];
	/** Saved value per existing job. A missing job is unknown (still loading or failed). */
	loadedSilentByJobId: ReadonlyMap<string, boolean>;
	overrides: JobSilentOverrides;
};

/** What the server will hold for this job after the job list saves. */
export const getBaselineSilent = (
	jobId: string,
	{ originalJobIds, finalJobIds, loadedSilentByJobId }: Omit<DraftContext, "overrides">,
): boolean | undefined => {
	if (becomesPrimary(originalJobIds, finalJobIds, jobId)) return false;
	return originalJobIds.includes(jobId) ? loadedSilentByJobId.get(jobId) : isNewJobSilentByDefault(finalJobIds, jobId);
};

/** The value a switch shows: the admin's choice, else the baseline. `undefined` means unknown. */
export const resolveDraftSilent = (jobId: string, context: DraftContext): boolean | undefined => {
	const override = context.overrides[jobId];
	return override === undefined ? getBaselineSilent(jobId, context) : override;
};

/**
 * Changes to send after the job list saves: held jobs whose switch differs from the baseline.
 * A choice made while the baseline is unknown is sent too, so the admin's choice is what gets stored.
 */
export const computeJobNotificationChanges = (context: DraftContext): JobNotificationChange[] =>
	context.finalJobIds.flatMap((jobId) => {
		const override = context.overrides[jobId];
		if (override === undefined) return [];
		const baseline = getBaselineSilent(jobId, context);
		return baseline === override ? [] : [{ jobId, isSilent: override }];
	});
